// src/components/SupportAndConnect.js
import React from 'react';
import { Card, CardContent, Typography, Box, Link as MuiLink, Divider, Paper } from '@mui/material';
import { FaTwitter, FaInstagram, FaAndroid, FaApple, FaEnvelope } from 'react-icons/fa';

/**
 * A smart, dynamic component for user engagement, partnership invitations, and platform announcements.
 */
const SupportAndConnect = () => {
  return (
    <Card className="myCard" sx={{ mt: 2, mb: 2 }}>
      <CardContent>
        {/* App Release Announcement Section */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: 'var(--color-primary-container, #e3f2fd)', borderLeft: '4px solid var(--color-accent-teal, #2A9D8F)' }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'var(--color-on-primary-container, #0d47a1)', display: 'flex', alignItems: 'center', gap: 1 }}>
            Mobile Apps Update!
          </Typography>
          <Typography variant="body1" paragraph sx={{ color: 'var(--color-on-surface-variant, #424242)' }}>
            The <strong>TrackCopr iOS app</strong> is now available on the App Store! Download it today to track your immigration journey on the go. The Android version is currently pending Google approval and will be available soon.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mt: 1, alignItems: 'center' }}>
            <MuiLink
              href="https://apps.apple.com/ca/app/track-copr-canada/id6748865450"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              <FaApple size="1.8rem" color="#000000" />
              <Typography variant="button" sx={{ fontWeight: 'bold' }}>Download on iOS</Typography>
            </MuiLink>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.6 }} title="Coming Soon">
              <FaAndroid size="1.8rem" color="var(--color-accent-teal, #2A9D8F)" />
              <Typography variant="button">Android (Soon)</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Partnership & Collaboration Section */}
        <Box mb={3}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'var(--color-primary)' }}>
            Partnership Opportunities
          </Typography>
          <Typography variant="body1" paragraph>
            Are you a licensed Canadian Immigration Consultant (RCIC) or a lawyer specializing in immigration? TrackCopr is looking to partner with professionals who are committed to providing exceptional service.
          </Typography>
          <Typography variant="body1" paragraph>
            We offer unique collaboration opportunities, including <strong>featured listings and dedicated staff access</strong> to our platform to manage client timelines and communications efficiently.
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 500 }}>
            Let's Connect!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            To discuss potential partnerships, please contact us via email or send a direct message to our social media channels.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
            <MuiLink href="https://x.com/trackcopr" target="_blank" rel="noopener noreferrer" aria-label="Twitter" sx={{ color: 'var(--color-primary)', '&:hover': { color: 'var(--color-accent-red)' }, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FaTwitter size="1.25rem" />
              <Typography variant="button">DM on Twitter</Typography>
            </MuiLink>
            <MuiLink href="https://www.instagram.com/trackcopr_com/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" sx={{ color: 'var(--color-primary)', '&:hover': { color: 'var(--color-accent-red)' }, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FaInstagram size="1.25rem" />
              <Typography variant="button">DM on Instagram</Typography>
            </MuiLink>
            <MuiLink href="mailto:admin@trackcopr.com" aria-label="Email" sx={{ color: 'var(--color-primary)', '&:hover': { color: 'var(--color-accent-red)' }, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FaEnvelope size="1.25rem" />
              <Typography variant="button">Email Us</Typography>
            </MuiLink>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Stay Connected & Feedback Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Stay Connected & Share Your Feedback
          </Typography>
          <Typography variant="body2" paragraph color="text.secondary">
            Follow us on social media for the latest updates, draw announcements, and to connect with the TrackCopr community. Your feedback is invaluable in shaping the future of this platform.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 2 }} />

        {/* A special note to my users */}
        <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'var(--color-background)', borderRadius: 'var(--border-radius)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'var(--color-accent-teal)'}}>A Quick Word to My Users!</Typography>
            <Typography variant="body1" paragraph>
                This platform is for you, and I want to hear from you! Slide into my DMs on Instagram with any feedback you have—the good, the bad, and the ugly. Seriously, all ideas and critiques are welcome. Let's build the best immigration tracking tool together.
            </Typography>
            <MuiLink href="https://www.instagram.com/trackcopr_com/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" sx={{ color: 'var(--color-primary)', '&:hover': { color: 'var(--color-accent-red)' }, display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold' }}>
              <FaInstagram size="1.25rem" />
              Message Me on Instagram
            </MuiLink>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            A special thank you to myself for bringing this amazing website to the world, with a little(LOTTA) help from GenAI, my friends and family (not in that order)!
          </Typography>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'right' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'var(--color-text-secondary)'}}>
                Thank you for your attention to this matter !! - VA
            </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SupportAndConnect;